import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  updateProfile,
  updatePassword,
  signOut as firebaseSignOut,
  EmailAuthProvider,
  reauthenticateWithCredential,
  sendEmailVerification,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, setDoc, getDoc, getDocFromServer, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from './firebase';

export interface SignUpData {
  email: string;
  password: string;
  name: string;
}

export interface LoginData {
  email: string;
  password: string;
}

export interface ProfileData {
  displayName: string;
}

export async function signUp({ email, password, name }: SignUpData) {
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  
  // Calculate trial end date (7 days from now)
  const trialEndDate = new Date();
  trialEndDate.setDate(trialEndDate.getDate() + 7);
  
  // Update user profile with name
  await updateProfile(userCredential.user, { displayName: name });
  
  // Send verification email
  await sendEmailVerification(userCredential.user);
  
  // Create user document in Firestore
  await setDoc(doc(db, 'users', userCredential.user.uid), {
    email,
    name,
    createdAt: new Date(),
    emailVerified: false,
    subscriptionStatus: 'free',
    subscriptionDate: new Date(),
    trialEndDate: trialEndDate,
    hasSeenWelcome: false
  });

  return userCredential.user;
}

export async function login({ email, password }: LoginData) {
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  
  // Check and update subscription status
  await checkAndUpdateSubscriptionStatus(userCredential.user.uid);
  
  return userCredential.user;
}

export async function signOut() {
  try {
    // Sign out from Firebase Auth
    // Firebase Auth handles session isolation automatically
    // Firestore security rules prevent unauthorized data access
    // No need to terminate the Firestore client (it's a singleton and would break the app)
    await firebaseSignOut(auth);

    console.log('User signed out successfully');
  } catch (error) {
    console.error('Error during sign out:', error);
    throw error;
  }
}

export async function resendVerificationEmail() {
  const user = auth.currentUser;
  if (!user) throw new Error('No authenticated user');
  
  if (user.emailVerified) {
    throw new Error('Email is already verified');
  }

  await sendEmailVerification(user);
}

export async function updateUserProfile(data: ProfileData) {
  const user = auth.currentUser;
  if (!user) throw new Error('No authenticated user');

  await updateProfile(user, data);
  
  // Update user document in Firestore
  await setDoc(doc(db, 'users', user.uid), {
    displayName: data.displayName
  }, { merge: true });
}

export async function updateUserPassword(currentPassword: string, newPassword: string) {
  const user = auth.currentUser;
  if (!user?.email) throw new Error('No authenticated user');

  try {
    // Re-authenticate user before password change
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    
    // Update password
    await updatePassword(user, newPassword);
  } catch (error: any) {
    if (error.code === 'auth/wrong-password') {
      throw new Error('Current password is incorrect');
    }
    throw error;
  }
}

export async function sendPasswordReset(email: string) {
  await sendPasswordResetEmail(auth, email);
}

export async function reauthenticateUser(password: string): Promise<void> {
  const user = auth.currentUser;
  if (!user?.email) throw new Error('No authenticated user');

  try {
    const credential = EmailAuthProvider.credential(user.email, password);
    await reauthenticateWithCredential(user, credential);
  } catch (error: any) {
    if (error.code === 'auth/wrong-password') {
      throw new Error('Incorrect password');
    }
    if (error.code === 'auth/invalid-credential') {
      throw new Error('Incorrect password');
    }
    throw error;
  }
}

// Check if trial has expired and update subscription status
export async function checkAndUpdateSubscriptionStatus(userId: string): Promise<boolean> {
  try {
    // Wait for auth state to be stable
    await new Promise<void>((resolve) => {
      const unsubscribe = auth.onAuthStateChanged((user) => {
        unsubscribe();
        resolve();
      });
    });

    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.error('No authenticated user when checking subscription status');
      return false;
    }

    // Verify the userId matches the current user
    if (currentUser.uid !== userId) {
      console.error('User ID mismatch when checking subscription status');
      return false;
    }

    const userRef = doc(db, 'users', userId);

    try {
      // Use getDocFromServer to bypass cache and ensure fresh data
      const userSnap = await getDocFromServer(userRef);
      
      if (!userSnap.exists()) {
        console.error('User document not found when checking subscription status');
        return false;
      }
      
      const userData = userSnap.data();
      
      // If subscription status is missing, set it to free by default
      if (!userData.subscriptionStatus) {
        try {
          await updateDoc(userRef, {
            subscriptionStatus: 'free',
            lastUpdated: serverTimestamp()
          });
        } catch (updateError: any) {
          console.error('Error updating missing subscription status:', updateError);
          return false;
        }
        return false;
      }
      
      // If user has a Stripe customer ID and paid status, they have a real paid subscription
      if (userData.stripeCustomerId && userData.subscriptionStatus === 'paid') {
        return true;
      }

      // Check if user has an active trial (free users with trial end date in the future)
      if (userData.subscriptionStatus === 'free' && userData.trialEndDate) {
        const trialEnd = userData.trialEndDate.toDate();
        const now = new Date();

        // If trial is still active, grant premium access
        if (trialEnd > now) {
          return true;
        }

        // Trial has ended, no premium access
        return false;
      }

      // Check if user is a paid subscriber (for backward compatibility)
      return userData.subscriptionStatus === 'paid';
    } catch (docError: any) {
      console.error('Error fetching user document:', docError);
      throw new Error(`Failed to fetch user document: ${docError.message}`);
    }
  } catch (error: any) {
    console.error('Detailed subscription status error:', error);
    throw new Error(`Error checking subscription status: ${error.message}`);
  }
}